import * as React from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Stack,
  Paper,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { supabase } from "../../logic/client";
import { useI18n } from "../../i18n/I18nProvider";
import { Category, categoryConfig, categoryOrder } from "./Utils";


type Question = {
  id: string;
  question_code?: string | null;
  question_en: string;
  question_de: string;
  options: {
    answer_en: string;
    answer_de: string;
    weight: number
  }[];
  order_index: number;
};

const AWARE_THREAT_WALL_RESUME_INDEX_KEY = "tk.awareThreatWallResumeIndex";
const LAB_SCORE_REFRESH_EVENT = "tk:labScoreRefresh";
const LAB_PHOTOS_BUCKET = "lab-photos";
const MAX_LAB_PHOTO_BYTES = 10 * 1024 * 1024;
const PREPARE_MODULE_CODES = ["M1", "M2", "M3", "M4", "M5", "M6", "M7", "M8"];
const CATEGORY_TITLE_DE: Record<Category, string> = {
  aware: "Bewusst",
  prepare: "Bereit",
  share: "Gemeinsam",
};

function toNumber(value: number | string | null | undefined) {
  const numeric = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizeAnswer(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function isPhotoTakenAnswer(value: string) {
  const normalized = normalizeAnswer(value);

  return (
    (normalized.includes("photo") &&
      (normalized.includes("taken") ||
        normalized.includes("done") ||
        normalized.includes("uploaded"))) ||
    (normalized.includes("foto") &&
      (normalized.includes("gemacht") ||
        normalized.includes("aufgenommen") ||
        normalized.includes("genommen") ||
        normalized.includes("hochgeladen")))
  );
}

function fileExtension(file: File) {
  const fromName = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (fromName) return fromName;
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  if (file.type === "image/gif") return "gif";
  return "jpg";
}

export default function VibeQuestionsFlow(props: {
  handlePageChange?: (page: string) => void;
  category: Category;
  moduleCode?: string | null;
}) {
  const [questions, setQuestions] = React.useState<Question[]>([]);
  const [scoreQuestions, setScoreQuestions] = React.useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [optionalText, setOptionalText] = React.useState<Record<string, string>>({}); // ✅ added
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [showThankYouDialog, setShowThankYouDialog] = React.useState(false);
  const [uploadingPhotoQuestionId, setUploadingPhotoQuestionId] = React.useState<string | null>(null);
  const [photoUploadError, setPhotoUploadError] = React.useState<string | null>(null);
  const [photoFileNames, setPhotoFileNames] = React.useState<Record<string, string>>({});
  const [verifiedPhotoPaths, setVerifiedPhotoPaths] = React.useState<Record<string, boolean>>({});
  const photoInputRef = React.useRef<HTMLInputElement | null>(null);

  const { lang } = useI18n();
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);
  const isAwareCategory = String(props.category).toLowerCase() === "aware";
  const isPrepareCategory = String(props.category).toLowerCase() === "prepare";
  const isShareCategory = String(props.category).toLowerCase() === "share";
  const prepareModuleCode = isPrepareCategory ? props.moduleCode ?? null : null;

  const isYes = (value: string) => {
    if (!value) return false;
    const v = value.toLowerCase().trim();

    return (
      v.startsWith("yes") ||
      v.startsWith("ja") ||
      v.includes("yes") ||
      v.includes("ja")
    );
  };

  const index = categoryOrder.indexOf(props.category);
  const nextCategory = categoryOrder[index + 1];

  const nextTopicName = nextCategory
    ? categoryConfig[nextCategory].title
    : "the workshop";

  const nextModuleName = nextTopicName;
  const workshopDate = new Date().toLocaleDateString(lang === "de" ? "de-DE" : "en-US");

  React.useEffect(() => {
    if (isAwareCategory) {
      const resumeIndex = Number(sessionStorage.getItem(AWARE_THREAT_WALL_RESUME_INDEX_KEY));

      if (Number.isInteger(resumeIndex) && resumeIndex > 0) {
        setCurrentIndex(resumeIndex);
        return;
      }
    }

    setCurrentIndex(0);
  }, [isAwareCategory, isShareCategory, props.category]);

  // INIT
  React.useEffect(() => {
    async function init() {
      setLoading(true);

      try {
        const { data: emp } = await supabase.rpc("my_employee_id");
        if (!emp) throw new Error("No employee id");

        setEmployeeId(emp);

        const scoreQuestionQuery = supabase
            .from("vibe_questions_v2")
            .select("*")
            .eq("category", props.category)
            .order("order_index", { ascending: true });

        let questionQuery = supabase
            .from("vibe_questions_v2")
            .select("*")
            .eq("category", props.category)
            .order("order_index", { ascending: true });

        if (prepareModuleCode) {
          questionQuery = questionQuery.eq("question_code", prepareModuleCode);
        }

        const [qRes, scoreQRes, aRes] = await Promise.all([
          questionQuery,
          scoreQuestionQuery,

          supabase
            .from("employee_vibe_answers_v2")
            .select("question_id, answer, optional"),
        ]);

        if (qRes.error) throw qRes.error;
        if (scoreQRes.error) throw scoreQRes.error;
        if (aRes.error) throw aRes.error;

        setQuestions(qRes.data || []);
        setScoreQuestions(scoreQRes.data || []);

        const mapped: Record<string, string> = {};
        const optionalMapped: Record<string, string> = {};

        (aRes.data || []).forEach((row) => {
          mapped[row.question_id] = row.answer;
          optionalMapped[row.question_id] = row.optional || "";
        });

        setAnswers(mapped);
        setOptionalText(optionalMapped);
      } catch (err) {
        console.error("init error", err);
      }

      setLoading(false);
    }

    init();
  }, [prepareModuleCode, props.category]);

  const currentQuestion = questions[currentIndex] ?? null;
  const selectedAnswer = currentQuestion ? answers[currentQuestion.id] || "" : "";
  const currentPhotoPath = currentQuestion ? optionalText[currentQuestion.id] || "" : "";
  const shouldVerifyCurrentPhoto = Boolean(
    isShareCategory &&
      currentQuestion &&
      isPhotoTakenAnswer(selectedAnswer) &&
      currentPhotoPath &&
      verifiedPhotoPaths[currentPhotoPath] !== true
  );

  // SAVE SINGLE ANSWER
  const saveSingleAnswer = React.useCallback(
    async (
      question_id: string,
      answer: string,
      optionalOverride?: string | null
    ) => {
      if (!employeeId) return;

      const question = questions.find((item) => item.id === question_id);
      const selectedOption = question?.options.find(
        (option) => option.answer_en === answer || option.answer_de === answer
      );
      const optionalValue =
        optionalOverride !== undefined
          ? optionalOverride
          : optionalText[question_id] || null;

      try {
        const { error } = await supabase
          .from("employee_vibe_answers_v2")
          .upsert(
            [
              {
                employee_id: employeeId,
                question_id,
                answer,
                weight: selectedOption?.weight ?? null,
                optional: optionalValue || null,
              },
            ],
            { onConflict: "employee_id,question_id" }
          );

        if (error) throw error;

        window.dispatchEvent(new Event(LAB_SCORE_REFRESH_EVENT));
      } catch (err) {
        console.error("saveSingleAnswer error", err);
      }
    },
    [employeeId, optionalText, questions]
  );

  React.useEffect(() => {
    if (!shouldVerifyCurrentPhoto || !currentQuestion) return;

    let cancelled = false;

    async function verifyPhotoExists() {
      const path = currentPhotoPath;
      const slashIndex = path.lastIndexOf("/");
      const folder = slashIndex >= 0 ? path.slice(0, slashIndex) : "";
      const fileName = slashIndex >= 0 ? path.slice(slashIndex + 1) : path;

      if (!folder || !fileName) {
        setOptionalText((prev) => ({ ...prev, [currentQuestion.id]: "" }));
        await saveSingleAnswer(currentQuestion.id, selectedAnswer, null);
        return;
      }

      const { data, error } = await supabase.storage
        .from(LAB_PHOTOS_BUCKET)
        .list(folder, { limit: 1, search: fileName });

      if (cancelled) return;

      const exists = !error && Boolean(data?.some((item) => item.name === fileName));

      if (exists) {
        setVerifiedPhotoPaths((prev) => ({ ...prev, [path]: true }));
        return;
      }

      setOptionalText((prev) => ({ ...prev, [currentQuestion.id]: "" }));
      setPhotoFileNames((prev) => ({ ...prev, [currentQuestion.id]: "" }));
      setPhotoUploadError(
        L(
          "The saved photo is no longer in storage. Please upload it again.",
          "Das gespeicherte Foto ist nicht mehr im Speicher. Bitte lade es erneut hoch."
        )
      );
      await saveSingleAnswer(currentQuestion.id, selectedAnswer, null);
    }

    verifyPhotoExists();

    return () => {
      cancelled = true;
    };
  }, [currentPhotoPath, currentQuestion, selectedAnswer, shouldVerifyCurrentPhoto, L, saveSingleAnswer]);

  // SELECT
  async function handleSelect(value: string) {
    const q = questions[currentIndex];
    if (!q) return;

    setAnswers((prev) => ({
      ...prev,
      [q.id]: value,
    }));

    const shouldClearOptional = !isYes(value) && !isPhotoTakenAnswer(value);
    const previousPhotoPath = optionalText[q.id];

    if (shouldClearOptional) {
      setOptionalText((prev) => ({
        ...prev,
        [q.id]: "",
      }));
      setPhotoFileNames((prev) => ({
        ...prev,
        [q.id]: "",
      }));
    }

    await saveSingleAnswer(q.id, value, shouldClearOptional ? null : optionalText[q.id] || null);

    if (shouldClearOptional && previousPhotoPath && isPhotoTakenAnswer(answers[q.id] || "")) {
      await supabase.storage.from(LAB_PHOTOS_BUCKET).remove([previousPhotoPath]);
    }
  }

  // OPTIONAL INPUT
  function handleOptionalChange(value: string) {
    const q = questions[currentIndex];
    if (!q) return;

    setOptionalText((prev) => ({
      ...prev,
      [q.id]: value,
    }));

    saveSingleAnswer(q.id, answers[q.id], value);
  }

  function next() {
    if (isAwareCategory && currentIndex === 0) {
      sessionStorage.setItem(AWARE_THREAT_WALL_RESUME_INDEX_KEY, "1");
      props.handlePageChange?.("AwareThreatWallScreen");
      return;
    }

    if (currentIndex < questions.length - 1) {
      if (isAwareCategory && currentIndex >= 1) {
        sessionStorage.removeItem(AWARE_THREAT_WALL_RESUME_INDEX_KEY);
      }
      setCurrentIndex((prev) => prev + 1);
    }
  }

  function back() {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      return;
    }

    if (externalBackPage) {
      props.handlePageChange?.(externalBackPage);
    }
  }

  async function submit() {
    if (!employeeId) return;

    if (isAwareCategory) {
      sessionStorage.removeItem(AWARE_THREAT_WALL_RESUME_INDEX_KEY);
    }

    setSubmitting(true);

    try {
      const rows = questions
        .filter((question) => answers[question.id])
        .map((question) => ({
        employee_id: employeeId,
        question_id: question.id,
        answer: answers[question.id],
        weight:
          question
            .options.find((option) => option.answer_en === answers[question.id] || option.answer_de === answers[question.id])
            ?.weight ?? null,
        optional: optionalText[question.id] || null,
      }));

      const { error } = await supabase
        .from("employee_vibe_answers_v2")
        .upsert(rows, { onConflict: "employee_id,question_id" });

      if (error) throw error;

      window.dispatchEvent(new Event(LAB_SCORE_REFRESH_EVENT));

      if (isAwareCategory && props.handlePageChange) {
        props.handlePageChange("AwareCrisisVideoScreen");
        return;
      }

      if (isPrepareCategory && props.handlePageChange) {
        if (prepareModuleCode) {
          const currentModule = Number(prepareModuleCode.replace("M", ""));
          const nextModule = currentModule + 1;
          props.handlePageChange(nextModule <= 8 ? `PrepareScenarioM${nextModule}` : "PrepareReflectionScreen");
          return;
        }

        props.handlePageChange("PrepareResultScreen");
        return;
      }

      if (props.category === "share" && props.handlePageChange) {
        props.handlePageChange("JourneyPillars");
        return;
      }

      setShowThankYouDialog(true);
    } catch (err) {
      console.error(err);
      alert("Error saving answers");
    }

    setSubmitting(false);
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography color="#fff">{L("Loading...", "Lädt...")}</Typography>
      </Box>
    );
  }

  if (!questions.length) return null;

  const current = questions[currentIndex];
  if (!current) return null;

  const selected = answers[current.id] || "";
  const requiresPhotoUpload = isShareCategory && isPhotoTakenAnswer(selected);
  const uploadedPhotoPath = optionalText[current.id] || "";
  const hasVerifiedUploadedPhoto = uploadedPhotoPath && verifiedPhotoPaths[uploadedPhotoPath] === true;
  const isVerifyingCurrentPhoto = Boolean(
    requiresPhotoUpload &&
      uploadedPhotoPath &&
      !hasVerifiedUploadedPhoto &&
      uploadingPhotoQuestionId !== current.id
  );
  const isUploadingCurrentPhoto = uploadingPhotoQuestionId === current.id;
  const canAdvance =
    Boolean(selected) &&
    (!requiresPhotoUpload || Boolean(hasVerifiedUploadedPhoto)) &&
    !isVerifyingCurrentPhoto &&
    !isUploadingCurrentPhoto;
  const prepareModuleIndex = prepareModuleCode
    ? PREPARE_MODULE_CODES.indexOf(prepareModuleCode)
    : -1;
  const progressCurrent =
    prepareModuleIndex >= 0 ? prepareModuleIndex + 1 : currentIndex + 1;
  const progressTotal =
    prepareModuleIndex >= 0 ? PREPARE_MODULE_CODES.length : questions.length;
  const progressPercent = (progressCurrent / progressTotal) * 100;
  const externalBackPage =
    isAwareCategory
      ? "AwareIntroScreen"
      : isPrepareCategory && prepareModuleCode
      ? `PrepareScenario${prepareModuleCode}`
      : isShareCategory
        ? "JourneyPillars"
        : null;
  const showScoreIncrease = isAwareCategory || isPrepareCategory;
  const scoreMax = isAwareCategory ? 60 : 40;
  const rawScoreMax = scoreQuestions.reduce((total, question) => {
    const maxOptionWeight = Math.max(
      0,
      ...question.options.map((option) => toNumber(option.weight))
    );

    return total + maxOptionWeight;
  }, 0);
  const previousScoreQuestions = scoreQuestions.filter((question) => {
    if (isPrepareCategory) {
      const moduleIndex = PREPARE_MODULE_CODES.indexOf(question.question_code ?? "");
      return moduleIndex >= 0 && moduleIndex < prepareModuleIndex;
    }

    return question.order_index < current.order_index;
  });
  const rawPreviousScore = previousScoreQuestions.reduce((total, question) => {
    const answer = answers[question.id];
    if (!answer) return total;

    const weight = question.options.find(
      (option) => option.answer_en === answer || option.answer_de === answer
    )?.weight;

    return total + toNumber(weight);
  }, 0);
  const displayedScore = rawScoreMax > 0 ? (rawPreviousScore / rawScoreMax) * scoreMax : 0;
  const previousImmediateQuestion =
    previousScoreQuestions[previousScoreQuestions.length - 1] ?? null;
  const previousImmediateAnswer = previousImmediateQuestion
    ? answers[previousImmediateQuestion.id]
    : "";
  const previousImmediateWeight = previousImmediateQuestion
    ? toNumber(
        previousImmediateQuestion.options.find(
          (option) =>
            option.answer_en === previousImmediateAnswer ||
            option.answer_de === previousImmediateAnswer
        )?.weight
      )
    : null;
  const previousQuestionWasNotRated =
    Boolean(previousImmediateAnswer) && previousImmediateWeight === 0;
  const hasPreviousScore = rawPreviousScore > 0;
  const scoreFormatter = new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", {
    maximumFractionDigits: 1,
  });

  async function handlePhotoUpload(file: File) {
    if (!employeeId) return;

    if (!file.type.startsWith("image/")) {
      setPhotoUploadError(L("Please choose an image file.", "Bitte wähle eine Bilddatei aus."));
      return;
    }

    if (file.size > MAX_LAB_PHOTO_BYTES) {
      setPhotoUploadError(
        L("The photo must be 10 MB or smaller.", "Das Foto darf maximal 10 MB groß sein.")
      );
      return;
    }

    setUploadingPhotoQuestionId(current.id);
    setPhotoUploadError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      if (!userId) {
        throw new Error(L("Session expired. Please log in again.", "Sitzung abgelaufen. Bitte melde dich erneut an."));
      }

      const previousPath = uploadedPhotoPath;
      const path = `${userId}/${employeeId}-${current.id}-${Date.now()}.${fileExtension(file)}`;
      const { error } = await supabase.storage.from(LAB_PHOTOS_BUCKET).upload(path, file, {
        cacheControl: "3600",
        contentType: file.type,
        upsert: false,
      });

      if (error) throw error;

      setOptionalText((prev) => ({
        ...prev,
        [current.id]: path,
      }));
      setPhotoFileNames((prev) => ({
        ...prev,
        [current.id]: file.name,
      }));
      setVerifiedPhotoPaths((prev) => ({
        ...prev,
        [path]: true,
      }));

      await saveSingleAnswer(current.id, selected, path);

      if (previousPath && previousPath !== path) {
        await supabase.storage.from(LAB_PHOTOS_BUCKET).remove([previousPath]);
      }
    } catch (err: any) {
      console.error("lab photo upload error", err);
      setPhotoUploadError(
        err?.message || L("Photo upload failed.", "Foto-Upload fehlgeschlagen.")
      );
    } finally {
      setUploadingPhotoQuestionId(null);
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: { xs: 4, sm: 5 },
          p: { xs: 3, sm: 5, md: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background: "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
        }}
      >
        {showScoreIncrease && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid rgba(255,255,255,0.12)",
              backgroundColor: "rgba(255,255,255,0.05)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={0.75}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: 14, fontWeight: 800 }}>
                {isAwareCategory
                  ? L("AWARE score", "Bewusst Score")
                  : L("PREPARE score increase", "Bereit Score-Zuwachs")}
              </Typography>
              <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 900 }}>
                {hasPreviousScore
                  ? `${isPrepareCategory ? "+" : ""}${scoreFormatter.format(displayedScore)} / ${
                      isPrepareCategory ? "+" : ""
                    }${scoreMax}`
                  : previousQuestionWasNotRated
                    ? L("Not rated", "Nicht bewertet")
                    : L("Not started yet", "Noch nicht gestartet")}
              </Typography>
            </Stack>
            <Typography sx={{ color: "rgba(255,255,255,0.54)", fontSize: 13, mt: 0.5 }}>
              {previousQuestionWasNotRated
                ? L(
                    "Previous question was not rated.",
                    "Die vorherige Frage wurde nicht bewertet."
                  )
                : L(
                    "Current answer is added on the next screen.",
                    "Die aktuelle Antwort wird auf der nächsten Seite eingerechnet."
                  )}
            </Typography>
          </Box>
        )}

        {/* PROGRESS */}
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
            {L(categoryConfig[props.category].title, CATEGORY_TITLE_DE[props.category])}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: 14, fontWeight: 700 }}>
            {progressCurrent} / {progressTotal}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 10,
            borderRadius: 999,
            mb: 4,
            backgroundColor: "rgba(255,255,255,0.14)",
            "& .MuiLinearProgress-bar": {
              background: "linear-gradient(90deg, #c50f22, #f05969)",
            },
          }}
        />

        {/* QUESTION */}
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            color: "#fff",
            fontSize: { xs: "1.4rem", sm: "1.7rem", md: "1.9rem" },
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {lang === "de" ? current.question_de : current.question_en}
        </Typography>

        <RadioGroup value={selected} onChange={(e) => handleSelect(e.target.value)}>
          {current.options.map((opt) => (
            <FormControlLabel
              key={opt.answer_en}
              value={lang === "de" ? opt.answer_de : opt.answer_en}
              control={
                <Radio
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    "&.Mui-checked": { color: "#f05969" },
                  }}
                />
              }
              label={lang === "de" ? opt.answer_de : opt.answer_en}
              sx={{
                color: "#fff",
                mx: 0,
                px: 2,
                py: 1.25,
                borderRadius: 2.5,
                mb: 1.25,
                border: "1px solid rgba(255,255,255,0.18)",
                backgroundColor: "rgba(255,255,255,0.04)",
                transition: "all .2s ease",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.09)",
                  borderColor: "rgba(255,255,255,0.34)",
                },
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: 15, sm: 17 },
                  fontWeight: 500,
                },
              }}
            />
          ))}
        </RadioGroup>

        {requiresPhotoUpload && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2.5,
              border: "1px solid rgba(255,255,255,0.18)",
              backgroundColor: "rgba(255,255,255,0.05)",
            }}
          >
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoUpload(file);
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Button
                variant="contained"
                startIcon={
                  isUploadingCurrentPhoto ? (
                    <CircularProgress size={18} sx={{ color: "#111827" }} />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                onClick={() => photoInputRef.current?.click()}
                disabled={isUploadingCurrentPhoto}
                sx={{
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                  textTransform: "none",
                  fontWeight: 800,
                  background: "#fff",
                  color: "#111827",
                  "&:hover": { background: "#f3f4f6" },
                }}
              >
                {hasVerifiedUploadedPhoto
                  ? L("Replace photo", "Foto ersetzen")
                  : L("Upload photo", "Foto hochladen")}
              </Button>

              {hasVerifiedUploadedPhoto && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                    {photoFileNames[current.id] || L("Photo uploaded", "Foto hochgeladen")}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {isVerifyingCurrentPhoto && (
              <Typography sx={{ color: "rgba(255,255,255,0.68)", fontSize: 13, mt: 1.5 }}>
                {L("Checking saved photo...", "Gespeichertes Foto wird geprüft...")}
              </Typography>
            )}

            {!uploadedPhotoPath && (
              <Typography sx={{ color: "rgba(255,255,255,0.68)", fontSize: 13, mt: 1.5 }}>
                {L(
                  "Upload the photo to continue.",
                  "Lade das Foto hoch, um fortzufahren."
                )}
              </Typography>
            )}

            {photoUploadError && (
              <Typography sx={{ color: "#ff8a8a", fontSize: 13, fontWeight: 700, mt: 1.5 }}>
                {photoUploadError}
              </Typography>
            )}
          </Box>
        )}

        {/* OPTIONAL FIELD */}
        {props.category === "share" && isYes(selected) && !requiresPhotoUpload && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={optionalText[current.id] || ""}
              onChange={(e) => handleOptionalChange(e.target.value)}
              placeholder={L(
                "Optional: tell us more...",
                "Optional: erzähl uns mehr..."
              )}
              sx={{
                mt: 1,
                '& .MuiInputBase-root': {
                  color: '#fff',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.7)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#fff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#fff',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255,255,255,0.5)',
                  opacity: 1,
                },
              }}
            />
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={back}
            disabled={currentIndex === 0 && !externalBackPage}
            sx={{
              borderColor: "rgba(255,255,255,0.4)",
              color: "#fff",
              px: 3,
              '&:hover': { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            {L("Back", "Zurück")}
          </Button>

          {currentIndex < questions.length - 1 ? (
            <Button
              variant="contained"
              onClick={next}
              disabled={!canAdvance}
              sx={{
                px: 3,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                fontWeight: 700,
              }}
            >
              {L("Continue", "Weiter")}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={submit}
              disabled={!canAdvance || submitting}
              sx={{
                px: 3,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                fontWeight: 700,
              }}
            >
              {isPrepareCategory ? L("Continue", "Weiter") : L("Submit", "Absenden")}
            </Button>
          )}
        </Stack>
      </Paper>
      <Dialog
        open={showThankYouDialog}
        onClose={() => setShowThankYouDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent
          sx={{
            background: "linear-gradient(180deg, #301f1f 0%, #221313 100%)",
            color: "#fff",
            borderTop: "4px solid #d6293e",
            p: { xs: 3, sm: 4 },
          }}
        >
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, mb: 2 }}>
            {L("Thank you!", "Danke!")}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.6 }}>
            {L(
              `Thank you for your feedback. Your answers contribute to your final Score for the workshop on ${workshopDate}. Please proceed to the next topic: ${nextTopicName}.`,
              `Danke für dein Feedback. Deine Antworten fließen in deinen finalen Score für den Workshop am ${workshopDate} ein. Bitte fahre mit dem nächsten Thema fort: ${nextTopicName}.`
            )}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 3, sm: 4 },
            pb: { xs: 3, sm: 4 },
            pt: 2,
            background: "linear-gradient(180deg, #221313 0%, #2d2929 100%)",
          }}
        >
          <Button
            variant="contained"
            onClick={() =>
              props.handlePageChange?.(
                nextModuleName === "the workshop"
                  ? "WorkshopDetails"
                  : "JourneyPillars"
              )
            }
            sx={{
              textTransform: "none",
              px: 3,
              background: "linear-gradient(90deg, #b50d20, #d6293e)",
              fontWeight: 700,
            }}
          >
            {L(`Continue with ${nextModuleName}`, `Weiter mit ${nextModuleName}`)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
